import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce, isEmpty } from 'lodash';
import {
  useGetAllProductsCalculations,
  useGetCalculationsOnProductUpdate,
} from 'services/calculations/salesPlan/hooks';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { initialPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';

import ToastService from 'helpers/ToastService';

import {
  getProductFieldsForSalesPlan,
  isAllSalesPlanRequiredFieldsFilled,
} from '../../helpers/salesPlan/salesPlanCalculation';
import { getUpdatedProductData, productChangeType } from '../../helpers/shared/shared';
import { IFormContentTypes, observedFields, PRODUCT_CHANGE_TYPE } from '../../utils/form';
import { useOrderTableValues } from '../useOrderTableValues';

export const useSalesPlanCalculation = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getUpdatedProductCalculation = useGetCalculationsOnProductUpdate();
  const getAllProductsCalculations = useGetAllProductsCalculations();

  const orderTableValues = useOrderTableValues();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (currentProductsArray, previousProductsArray, tableName, totalFieldName, unit) => {
        const updatedProduct = getUpdatedProductData(currentProductsArray, previousProductsArray);

        if (updatedProduct) {
          if (!isAllSalesPlanRequiredFieldsFilled(updatedProduct)) return;

          const body = {
            unit,
            id: updatedProduct.productId || '',
            businessPlanId,
            product: getProductFieldsForSalesPlan(updatedProduct),
          };
          const updatedCalculation = await getUpdatedProductCalculation.mutateAsync(body);

          setValue(tableName, orderTableValues(currentProductsArray, updatedCalculation.revenueData));
          setValue(totalFieldName, updatedCalculation.totalRevenueData);
        } else {
          // currency update calculation
          const calculationAfterDelete = await getAllProductsCalculations.mutateAsync({ businessPlanId, unit });
          if (isEmpty(calculationAfterDelete.revenueData)) {
            setValue(totalFieldName, initialPeriodsTableRow);
          } else {
            setValue(tableName, orderTableValues(currentProductsArray, calculationAfterDelete.revenueData));
            setValue(totalFieldName, calculationAfterDelete.totalRevenueData);
          }
        }
      }, 300),
     
    [],
  );

  const salesPlanCalculationHandler = useCallback(
    async (content: IFormContentTypes['salesPlan'], previousContent: IFormContentTypes['salesPlan']) => {
      if (!canAccess) return;

      const [tableName, totalFieldName] = observedFields.salesPlan.setCalculatedValueFieldNames;
      const currentProductsArray = content[0];
      const unit = content[1] || DIMENSIONALITY_VALUES.SINGLE;
      const previousProductsArray = previousContent[0];

      const changeType = productChangeType(currentProductsArray, previousProductsArray);

      try {
        if (changeType === PRODUCT_CHANGE_TYPE.REMOVE) {
          const calculationAfterDelete = await getAllProductsCalculations.mutateAsync({ businessPlanId, unit });
          if (isEmpty(calculationAfterDelete.revenueData)) {
            setValue(totalFieldName, initialPeriodsTableRow);
          } else {
            setValue(tableName, orderTableValues(currentProductsArray, calculationAfterDelete.revenueData));
            setValue(totalFieldName, calculationAfterDelete.totalRevenueData);
          }
        } else if (changeType === PRODUCT_CHANGE_TYPE.UPDATE) {
          debouncedUpdateFunction(currentProductsArray, previousProductsArray, tableName, totalFieldName, unit);
        }
      } catch (error) {
        if (error instanceof Error) ToastService.showError(error.message);
      }
    },
     
    [debouncedUpdateFunction],
  );

  return salesPlanCalculationHandler;
};
